/**
 * Accordion Section Framework
 * POV-0041: Implement accordion section framework
 * 
 * Provides the 11 accordion sections for the Part Overview Drawer.
 * Features: Multi-expand, expand/collapse all, section registry
 */
import React, { useState, useCallback, createContext, useContext } from 'react';
import { ChevronRight, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';

// Section Registry - stable keys per PRD spec (Section 5.2)
export const SECTION_REGISTRY = [
  { key: 'overview', label: 'Overview', order: 1, defaultExpanded: true },
  { key: 'identification_classification', label: 'Identification & Classification', order: 2 },
  { key: 'revision_configuration', label: 'Revision & Configuration', order: 3 },
  { key: 'requirements_documents', label: 'Requirements & Documents', order: 4 },
  { key: 'approved_sources_external_providers', label: 'Approved Sources & External Providers', order: 5 },
  { key: 'inventory', label: 'Inventory', order: 6 },
  { key: 'traceability_genealogy', label: 'Traceability & Genealogy', order: 7 },
  { key: 'inspection_test_release', label: 'Inspection, Test & Release', order: 8 },
  { key: 'nonconformance_mrb_concessions', label: 'Nonconformance / MRB / Concessions', order: 9 },
  { key: 'counterfeit_prevention', label: 'Counterfeit Prevention', order: 10 },
  { key: 'history', label: 'History', order: 11 },
];

// Get section keys as array
export const SECTION_KEYS = SECTION_REGISTRY.map(s => s.key);

// Accordion Context for managing expand/collapse state
const AccordionContext = createContext(null);

export const useAccordion = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordion must be used within AccordionProvider');
  }
  return context;
};

/**
 * Accordion Provider - manages expand/collapse state for all sections
 */
export const AccordionProvider = ({ children, defaultExpanded = ['overview'] }) => {
  const [expandedSections, setExpandedSections] = useState(new Set(defaultExpanded));

  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  }, []);

  const expandSection = useCallback((sectionKey) => {
    setExpandedSections(prev => new Set([...prev, sectionKey]));
  }, []);

  const collapseSection = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(new Set(SECTION_KEYS));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  const isExpanded = useCallback((sectionKey) => {
    return expandedSections.has(sectionKey);
  }, [expandedSections]);

  const value = {
    expandedSections,
    toggleSection,
    expandSection,
    collapseSection,
    expandAll,
    collapseAll,
    isExpanded,
  };

  return (
    <AccordionContext.Provider value={value}>
      {children}
    </AccordionContext.Provider>
  );
};

/**
 * Accordion Controls - Expand All / Collapse All / Jump to Section
 */
export const AccordionControls = ({ onJumpToSection }) => {
  const { expandAll, collapseAll, expandedSections } = useAccordion();
  const [jumpMenuOpen, setJumpMenuOpen] = useState(false);

  const allExpanded = expandedSections.size === SECTION_KEYS.length;
  const allCollapsed = expandedSections.size === 0;

  return (
    <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
      <button
        onClick={expandAll}
        disabled={allExpanded}
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
          allExpanded
            ? "text-slate-400 bg-slate-100 cursor-not-allowed"
            : "text-slate-600 bg-white border border-slate-300 hover:bg-slate-50"
        )}
        data-testid="expand-all-button"
      >
        Expand All
      </button>
      <button
        onClick={collapseAll}
        disabled={allCollapsed}
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
          allCollapsed
            ? "text-slate-400 bg-slate-100 cursor-not-allowed"
            : "text-slate-600 bg-white border border-slate-300 hover:bg-slate-50"
        )}
        data-testid="collapse-all-button"
      >
        Collapse All
      </button>
      
      {/* Jump to Section Dropdown */}
      <div className="relative ml-auto">
        <button
          onClick={() => setJumpMenuOpen(!jumpMenuOpen)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
          data-testid="jump-to-section-button"
        >
          <ChevronsUpDown className="w-4 h-4" />
          Jump to section
        </button>
        
        {jumpMenuOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setJumpMenuOpen(false)}
            />
            <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 max-h-80 overflow-y-auto">
              {SECTION_REGISTRY.map((section) => (
                <button
                  key={section.key}
                  onClick={() => {
                    onJumpToSection?.(section.key);
                    setJumpMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid={`jump-to-${section.key}`}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Accordion Section - Individual collapsible section
 */
export const AccordionSection = ({
  sectionKey,
  label,
  lastUpdated,
  children,
  isLoading = false,
  className,
}) => {
  const { isExpanded, toggleSection } = useAccordion();
  const expanded = isExpanded(sectionKey);

  return (
    <div 
      className={cn("border-b border-slate-200", className)}
      data-testid={`section-${sectionKey}`}
      id={`section-${sectionKey}`}
    >
      {/* Section Header */}
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
        aria-controls={`section-content-${sectionKey}`}
        data-testid={`section-header-${sectionKey}`}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          <span className="font-medium text-slate-900">{label}</span>
        </div>
        {lastUpdated && (
          <span className="text-xs text-slate-500">
            Last updated: {lastUpdated}
          </span>
        )}
      </button>

      {/* Section Content */}
      <div
        id={`section-content-${sectionKey}`}
        role="region"
        aria-labelledby={`section-header-${sectionKey}`}
        className={cn(
          "overflow-hidden transition-all duration-200 ease-in-out",
          expanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-4 pb-4">
          {isLoading ? (
            <SectionSkeleton />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Section Skeleton - Loading state for section content
 * POV-0043: Skeleton loading
 */
export const SectionSkeleton = () => (
  <div className="space-y-3 animate-pulse" data-testid="section-skeleton">
    <div className="h-4 bg-slate-200 rounded w-3/4" />
    <div className="h-4 bg-slate-200 rounded w-1/2" />
    <div className="h-4 bg-slate-200 rounded w-5/6" />
    <div className="grid grid-cols-2 gap-4 mt-4">
      <div className="h-20 bg-slate-100 rounded" />
      <div className="h-20 bg-slate-100 rounded" />
    </div>
  </div>
);

/**
 * Drawer Skeleton - Full drawer loading state
 * POV-0043: Skeleton loading
 */
export const DrawerSkeleton = () => (
  <div className="animate-pulse" data-testid="drawer-skeleton">
    {/* Header skeleton */}
    <div className="p-4 border-b border-slate-200">
      <div className="h-6 bg-slate-200 rounded w-48 mb-2" />
      <div className="h-4 bg-slate-100 rounded w-64 mb-2" />
      <div className="flex gap-2">
        <div className="h-6 bg-slate-100 rounded w-16" />
        <div className="h-6 bg-slate-100 rounded w-20" />
      </div>
    </div>
    
    {/* Sections skeleton */}
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded w-32" />
          </div>
          <div className="h-3 bg-slate-100 rounded w-24" />
        </div>
      </div>
    ))}
  </div>
);

export default AccordionSection;
